import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, CircleAlert, ListChecks, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  GAS_PROBLEMS,
  SLOT_LABELS,
  UNIT_LABELS,
  checkGasAnswer,
  formatQuantity,
  gasWorking,
  type AnswerCheck,
  type GasMode,
  type GasSlot,
} from './gasLaws';

const STATUS_STYLES: Record<AnswerCheck['status'], string> = {
  correct: 'border-brand-mint/45 bg-wash-mint',
  misconception: 'border-brand-coral/45 bg-wash-coral',
  incorrect: 'border-brand-coral/45 bg-wash-coral',
  invalid: 'border-brand-sunshine/50 bg-wash-sunshine',
};

export function AnswerFeedback({ check, working }: { check: AnswerCheck; working: string[] }) {
  return (
    <div role="status" className={`rounded-[1.5rem] border p-4 ${STATUS_STYLES[check.status]}`}>
      <p className="flex items-center gap-2 font-extrabold text-ink">
        {check.status === 'correct' ? (
          <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
        ) : check.status === 'invalid' ? (
          <CircleAlert className="h-5 w-5 text-brand-coral" aria-hidden="true" />
        ) : (
          <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
        )}
        {check.title}
      </p>
      <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{check.feedback}</p>
      {check.status !== 'invalid' && working.length > 0 ? (
        <ol
          className="mt-3 space-y-1 rounded-2xl bg-white/80 px-4 py-3 text-sm font-bold text-ink"
          aria-label="Model working"
        >
          {working.map((line) => (
            <li key={line} className="break-words">
              {line}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

export function GasLawsProblems({ mode }: { mode: GasMode }) {
  const problems = GAS_PROBLEMS[mode];
  const [problemId, setProblemId] = useState(problems[0].id);
  const [answer, setAnswer] = useState('');
  const [check, setCheck] = useState<AnswerCheck | null>(null);
  const problem = problems.find((candidate) => candidate.id === problemId) ?? problems[0];
  useScienceTutorSection('GasLawsProblems', { problemId, answer, question: problem.context, givens: problem.givens, unknown: problem.unknown, answerUnit: problem.answerUnit });

  const choose = (id: string) => {
    setProblemId(id);
    setAnswer('');
    setCheck(null);
  };

  return (
    <section
      aria-label="Gas law problems"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-sunshine text-ink">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Calculate, then check
          </p>
          <h2 className="text-xl font-extrabold text-ink">{problems.length} reviewed problems</h2>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Choose a problem">
        {problems.map((candidate, index) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === problem.id}
            onClick={() => choose(candidate.id)}
            title={candidate.title}
            className={`rounded-full border px-3 py-1.5 text-sm font-extrabold ${
              candidate.id === problem.id
                ? 'border-brand-sky bg-wash-sky text-ink'
                : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
            }`}
          >
            {index + 1}
            <span className="sr-only">. {candidate.title}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-ink">{problem.title}</h3>
          <p className="mt-2 font-medium leading-7 text-ink-soft">{problem.context}</p>
          <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {(Object.entries(problem.givens) as [GasSlot, (typeof problem.givens)[GasSlot]][]).map(
              ([slot, given]) =>
                given ? (
                  <div key={slot} className="rounded-2xl bg-surface px-3 py-2">
                    <dt className="text-xs font-extrabold text-slate2">{SLOT_LABELS[slot]}</dt>
                    <dd className="font-extrabold tabular-nums text-ink">
                      {formatQuantity(given)}
                    </dd>
                  </div>
                ) : null,
            )}
            <div className="rounded-2xl border-2 border-dashed border-brand-coral/40 px-3 py-2">
              <dt className="text-xs font-extrabold text-slate2">{SLOT_LABELS[problem.unknown]}</dt>
              <dd className="font-extrabold text-brand-coral">?</dd>
            </div>
          </dl>
        </div>
        <div className="min-w-0 space-y-3">
          <label htmlFor="gas-answer" className="block text-sm font-extrabold text-ink">
            Your answer for {SLOT_LABELS[problem.unknown]} ({UNIT_LABELS[problem.answerUnit]})
          </label>
          <div className="flex gap-2">
            <input
              id="gas-answer"
              type="number"
              step="any"
              inputMode="decimal"
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setCheck(null);
              }}
              className="min-w-0 flex-1 rounded-xl border border-hairline bg-surface px-4 py-3 font-bold text-ink focus:border-brand-sky focus:outline-none focus:ring-4 focus:ring-brand-sky/15"
            />
            <span className="grid place-items-center rounded-xl bg-surface px-3 font-extrabold text-slate2">
              {UNIT_LABELS[problem.answerUnit]}
            </span>
          </div>
          <button
            type="button"
            disabled={!answer.trim()}
            onClick={() => setCheck(checkGasAnswer(problem, Number(answer)))}
            className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Check answer
          </button>
          {check ? <AnswerFeedback check={check} working={gasWorking(problem)} /> : null}
        </div>
      </div>
    </section>
  );
}
